import { forwardRef, use, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ReactComponent as NoEvent } from "../../../res/svg/empty_state_event.svg";
import {
    doc,
    getDoc,
    Timestamp,
} from "firebase/firestore";
import { auth, firestore } from "../../../App";
import CheckListItem from "../form/CheckList";
import Loader from "../Loader";
import { FilledButton, OutlinedButton } from "../buttons/CommonButton";
import { CorzTextField } from "../form/TextField";
import Switch from "../form/Switch";
import { Divider, TipAlertBox } from "../Components";
import ModalDialogComponent from "../dialog/ModalDialog";
import { daysBetweenDates, formatDate, formatDateServer, stringToDate } from "../../../tools/DateTool";
import { deleteEvent, pushEvent, updateEvent } from "../../../tools/FirestoreTool";
import { eventEmitter } from "../../../tools/EventEmitter";

const Container = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 24px;
	max-width: 800px;
	margin: 24px auto 0;
	-webkit-user-select: none;
	user-select: none;
`;

const BookEventContainer = styled.div`
	display: flex;
	flex-direction: column;
	background: linear-gradient(
		20deg,
		rgba(from ${(props) => props.color} r g b / 40%) 0%,
		rgba(from var(--color-surface-container-low) r g b / 40%) 50%
	);
	box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
	padding: 12px 24px;
	border-radius: 12px;
	gap: 12px;
`;

const BookEventButtonContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 12px;
	justify-content: end;
	align-items: center;
	margin-top: 12px;
`;

/* Booking event style [START] */
const BookEventDates = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 12px;
	justify-content: space-around;
	align-items: center;
`;

const BookEventServicing = styled.div`
	display: flex;
	flex-direction: row;
	gap: 12px;
	align-items: center;
`;

const BookEventCheckout = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;

	ul {
		opacity: ${(props) => (props.servicing ? "0.6" : "1")};
		transition: 0.3s;
	}

	ul li {
		position: relative;
		width: max-content;
	}

	ul li::before {
		content: "";
		position: absolute;
		top: calc(50% - 1px);
		left: 0;
		width: ${(props) => (props.servicing ? "100%" : "0%")};
		height: 2px;
		background: var(--color-error);
		transition: 0.3s;
	}
`;
/* Booking event style [END] */

/* Booked event style [START] */
const BookedEventStatus = styled.div`
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	gap: 6px;
	align-items: baseline;
	justify-content: space-evenly;
	margin: 12px 0;
`;
/* Booked event style [END] */

const EventShow = forwardRef(
    ({ event, onBookingDaysChange = () => { } }, ref) => {
        const [eventData, setEventData] = useState(null);
        const [eventType, setEventType] = useState(null);

        useEffect(() => {
            console.log("Event changed: ", event);
            if (
                !event ||
                event == null ||
                (Array.isArray(event) && event.length === 0)
            ) {
                setEventType(null);
                return;
            }

            if (Array.isArray(event)) {
                setEventType(
                    event.some((e) => e.event === true) ? "booked" : "booking"
                );
            } else {
                setEventType(event.event ? "booked" : "booking");
            }

            setEventData(event);
        }, [event]);

        const changeViewHandler = (view, data) => {
            console.log(view, data);
            setEventData({
                ...eventData,
                ...data,
            });
            setEventType(view);
        };

        return (
            <Container>
                {!eventType ? (
                    <EmptyState />
                ) : eventType === "booking" ? (
                    <BookEvent
                        ref={ref}
                        event={eventData}
                        eventType={eventType}
                        onDaysChanged={onBookingDaysChange}
                        onChangeView={changeViewHandler}
                    />
                ) : (
                    <BookedEvent
                        ref={ref}
                        event={eventData}
                        eventType={eventType}
                        onDaysChanged={onBookingDaysChange}
                        onChangeView={changeViewHandler}
                    />
                )}
            </Container>
        );
    }
);

const EmptyState = () => {
    return (
        <div>
            <h2>Nessun evento selezionato</h2>
            <p>
                Seleziona una data sul calendario per vedere i dettagli della
                prenotazione o seleziona un range di date per prenotare.
            </p>
            <NoEvent style={{ width: "100%" }} />
        </div>
    );
};

const BookEvent = forwardRef(
    (
        { event, eventType, onDaysChanged = () => { }, onChangeView = () => { } },
        ref
    ) => {
        const startDateRef = useRef(null);
        const endDateRef = useRef(null);

        const [startDate, setStartDate] = useState(null);
        const [endDate, setEndDate] = useState(null);
        const [userColor, setUserColor] = useState("trasparent");
        const [nights, setNights] = useState(0);
        const [servicing, setServicing] = useState(false);
        const [tryBooking, setTryBooking] = useState(false);

        const switchRef = useRef(null);

        useEffect(() => {
            if (eventType !== "booking" || !event) return;

            if (Array.isArray(event)) {
                setStartDate(event[0].date);
                setEndDate(event[event.length - 1].date);
                setNights(
                    switchRef.current.isChecked()
                        ? 0
                        : daysBetweenDates(
                            event[0].date,
                            event[event.length - 1].date
                        )
                );
            } else {
                setStartDate(event.date);
                setEndDate(event.date);
                setNights(0);
            }
        }, [event, eventType]);

        useEffect(() => {
            if (!auth.currentUser) return;

            const userDoc = doc(firestore, "users", auth.currentUser.uid);
            getDoc(userDoc).then((docSnap) => {
                if (docSnap.exists()) {
                    setUserColor(docSnap.data().colore);
                }
            });
        }, [auth.currentUser]);

        const checkChangeHandler = (isChecked) => {
            setServicing(isChecked);

            if (isChecked) {
                setNights(0);
            } else {
                setNights(daysBetweenDates(startDate, endDate));
            }
        };

        const bookEventHandler = () => {
            setTryBooking(true);

            const data = {
                startDate: formatDateServer(startDate),
                endDate: formatDateServer(endDate),
                servicing: switchRef.current.isChecked(),
                cost: nights * 50,
                checkin: false,
                checkout: false,
                payed: false,
                timestampStartDate: Timestamp.fromDate(startDate),
                uid: auth.currentUser.uid,
            };

            console.log(data);

            pushEvent(data).then((eventId) => {
                eventEmitter.emit("showNotification", {
                    title: "Prenotazione avvenuta",
                    text: "La prenotazione è andata a buon fine!",
                });

                onDaysChanged(startDate, endDate);

                const newEvent = {
                    event: true,
                    uid: auth.currentUser.uid,
                    cid: eventId.id,
                };

                onChangeView("booked", newEvent);
            }).catch((error) => {
                console.error("Error adding document: ", error);

                eventEmitter.emit("showNotification", {
                    title: "Impossibile prenotare",
                    text: "La prenotazione non è andata a buon fine, riprova più tardi.",
                });
            }).finally(() => {
                setTryBooking(false);
            });
        };

        const dateChangeHandler = (ref, value) => {
            const date = new Date(value);
            date.setHours(0, 0, 0, 0);

            if (ref === startDateRef) {
                setStartDate(date);
                onDaysChanged(date, endDate);
            } else if (ref === endDateRef) {
                setEndDate(date);
                onDaysChanged(startDate, date);
            }
        };

        return (
            <BookEventContainer color={userColor}>
                <h2>Prenotazione</h2>
                <BookEventDates>
                    <CorzTextField
                        ref={startDateRef}
                        label="Data di inizio"
                        type="date"
                        value={formatDate(startDate)}
                        onValueChange={dateChangeHandler}
                    />
                    <CorzTextField
                        ref={endDateRef}
                        label="Data di fine"
                        type="date"
                        value={formatDate(endDate)}
                        onValueChange={dateChangeHandler}
                    />
                </BookEventDates>
                <BookEventServicing>
                    <Switch
                        ref={switchRef}
                        onCheckChange={checkChangeHandler}
                    />
                    <p>Prenotazione per servizio di manutenzione/lavori</p>
                </BookEventServicing>
                <TipAlertBox>
                    <span className="material-symbol fill">lightbulb</span>
                    <p>
                        Per la biancheria da letto ognuno deve portare da casa
                        un coprimaterasso, un copripiumino ed un copricuscino.
                        <br />
                        <b>Non usare i letti senza quest’ultimi!</b>
                    </p>
                </TipAlertBox>
                <Divider />
                <BookEventCheckout servicing={servicing}>
                    <h3>Ta ghet da pagá</h3>
                    <p>
                        Qui puoi trovare una stima iniziale dei costi di
                        utilizzo (l'energia consumata non è inclusa nel
                        calcolo). Il costo del soggiorno è di 50 franchi a
                        notte. Se la prenotazione è per manutenzione o lavori,
                        non ci sono costi da pagare.
                    </p>
                    <ul>
                        <li>
                            Tasso di soggiorno per {nights} notte/i: CHF{" "}
                            {nights * 50}.-
                        </li>
                        <li>
                            Energia elettrica consumata: CHF 0.- (Calcolata al
                            checkout)
                        </li>
                    </ul>
                </BookEventCheckout>
                <BookEventButtonContainer>
                    {tryBooking && <Loader size="24" />}
                    <FilledButton
                        text="Prenota"
                        theme="secondary"
                        onClick={bookEventHandler}
                        isDisabled={tryBooking}
                    >
                        <span className="material-symbol fill">bookmark</span>
                    </FilledButton>
                </BookEventButtonContainer>
            </BookEventContainer>
        );
    }
);

const BookedEvent = forwardRef(
    (
        { event, eventType, onDaysChanged = () => { }, onChangeView = () => { } },
        ref
    ) => {
        if (event != null && Array.isArray(event)) {
            for (let i = 0; i < event.length; i++) {
                if (event[i].event === true) {
                    event = event[i];
                    break;
                }
            }
        }

        const [eventData, setEventData] = useState(null);
        const [userData, setUserData] = useState(null);
        const [loadedEventId, setLoadedEventId] = useState(null);
        const [isYour, setIsYour] = useState(false);

        const [editMode, setEditMode] = useState(false);
        const [startDate, setStartDate] = useState(null);
        const [endDate, setEndDate] = useState(null);

        const [trySaving, setTrySaving] = useState(false);

        const startDateRef = useRef(null);
        const endDateRef = useRef(null);
        const dialogRef = useRef(null);

        useEffect(() => {
            let isMounted = true;
            console.log("Fetch data", event);

            const fetchData = async () => {
                const eventDoc = doc(firestore, "events", event.cid);
                const userDoc = doc(firestore, "users", event.uid);
                setIsYour(String(auth.currentUser.uid) === String(event.uid));

                try {
                    const eventSnap = await getDoc(eventDoc);
                    const userSnap = await getDoc(userDoc);

                    if (isMounted && eventSnap.exists() && userSnap.exists()) {
                        console.log(eventSnap, userSnap);

                        setEventData(eventSnap.data());
                        setUserData(userSnap.data());
                        setLoadedEventId(event.cid);
                    }
                } catch (error) {
                    console.error("Error loading event data:", error);
                }
            };

            if (
                eventType === "booked" &&
                event != null &&
                event.cid != null &&
                event.cid !== loadedEventId
            )
                fetchData();

            return () => {
                isMounted = false;
            };
        }, [event, eventType]);

        useEffect(() => {
            if (!eventData) return;

            const startDate = stringToDate(eventData.startDate);
            const endDate = stringToDate(eventData.endDate);

            setStartDate(startDate);
            setEndDate(endDate);

            onDaysChanged(startDate, endDate);
        }, [eventData]);

        const dateChangeHandler = (ref, value) => {
            const date = new Date(value);
            date.setHours(0, 0, 0, 0);

            if (ref === startDateRef) {
                setStartDate(date);
                onDaysChanged(date, endDate);
            } else if (ref === endDateRef) {
                setEndDate(date);
                onDaysChanged(startDate, date);
            }
        };

        const saveEditHandler = () => {
            setTrySaving(true);

            try {
                const data = {
                    startDate: formatDateServer(startDate),
                    endDate: formatDateServer(endDate),
                    cost: daysBetweenDates(startDate, endDate) * 50,
                };

                updateEvent(loadedEventId, data);

                eventEmitter.emit("showNotification", {
                    title: "Prenotazione aggiornata",
                    text: "La prenotazione è stata aggiornata!",
                });

                setEventData((prevState) => ({
                    ...prevState,
                    startDate: data.startDate,
                    endDate: data.endDate,
                    cost: data.cost,
                }));
                onDaysChanged(startDate, endDate);
            } catch (error) {
                console.error("Error updating document: ", error);
                eventEmitter.emit("showNotification", {
                    title: "Impossibile aggiornare la prenotazione",
                    text: "La prenotazione non è stata aggiornata.",
                });
            } finally {
                setTrySaving(false);
            }

            setEditMode(false);
        };

        const deleteEventHandler = () => {
            setTrySaving(true);

            try {
                deleteEvent(loadedEventId);

                eventEmitter.emit("showNotification", {
                    title: "Prenotazione eliminata",
                    text: "La prenotazione è stata eliminata!",
                });

                dialogRef.current.close();
                onDaysChanged(null, null);
                onChangeView(null, []);
            } catch (error) {
                console.error("Error deleting document: ", error);

                eventEmitter.emit("showNotification", {
                    title: "Impossibile eliminare la prenotazione",
                    text: "La prenotazione non è stata eliminata.",
                });
            } finally {
                setTrySaving(false);
            }
        };

        if (!eventData || !userData) return <Loader size="80" align="center" />;

        return (
            <BookEventContainer color={userData.colore}>
                <h2>
                    <span className="material-symbol fill">
                        {eventData.servicing ? "handyman" : "trip"}
                    </span>
                    {isYour
                        ? ` La tua prenotazione`
                        : ` Prenotazione di ${userData.nome}`}
                </h2>
                <h3>{`Dal ${eventData.startDate} al ${eventData.endDate}`}</h3>
                {isYour && editMode && (
                    <div>
                        <BookEventDates>
                            <CorzTextField
                                ref={startDateRef}
                                label="Data di inizio"
                                type="date"
                                value={formatDate(startDate)}
                                onValueChange={dateChangeHandler}
                            />
                            <CorzTextField
                                ref={endDateRef}
                                label="Data di fine"
                                type="date"
                                value={formatDate(endDate)}
                                onValueChange={dateChangeHandler}
                            />
                        </BookEventDates>
                        <BookEventButtonContainer>
                            {trySaving && <Loader size="24" />}
                            <OutlinedButton
                                text="Annulla"
                                theme="error"
                                isDisabled={trySaving}
                                onClick={() => {
                                    setEditMode(false);
                                }}
                            >
                                <span className="material-symbol fill">
                                    close
                                </span>
                            </OutlinedButton>
                            <FilledButton
                                text="Salva"
                                theme="secondary"
                                isDisabled={trySaving}
                                onClick={saveEditHandler}
                            >
                                <span className="material-symbol fill">
                                    check
                                </span>
                            </FilledButton>
                        </BookEventButtonContainer>
                    </div>
                )}
                {isYour && !editMode && (
                    <div>
                        <BookedEventStatus>
                            <CheckListItem
                                id="checkin"
                                name="status"
                                checkable={false}
                                isChecked={eventData.checkin}
                                label="Check-in"
                            />
                            <CheckListItem
                                id="checkout"
                                name="status"
                                checkable={false}
                                isChecked={eventData.checkout}
                                label="Check-out"
                            />
                            <CheckListItem
                                id="payed"
                                name="status"
                                checkable={false}
                                isChecked={eventData.payed}
                                label="Pagato"
                            />
                        </BookedEventStatus>
                        <BookEventButtonContainer>
                            {trySaving && <Loader size="24" />}
                            <OutlinedButton
                                text="Elimina"
                                theme="error"
                                isDisabled={(trySaving || eventData.checkin)}
                                onClick={() => {
                                    dialogRef.current.open();
                                }}
                            >
                                <span className="material-symbol fill">
                                    delete
                                </span>
                            </OutlinedButton>
                            <FilledButton
                                text="Modifica"
                                theme="secondary"
                                isDisabled={trySaving}
                                onClick={() => setEditMode(true)}
                            >
                                <span className="material-symbol fill">
                                    edit
                                </span>
                            </FilledButton>
                        </BookEventButtonContainer>
                    </div>
                )}
                <ModalDialogComponent
                    ref={dialogRef}
                    title="Elimina prenotazione"
                    icon="delete"
                    theme="var(--color-error)"
                    cancelButton={{
                        text: "Annulla",
                        theme: "error",
                        onClick: () => dialogRef.current.close(),
                    }}
                    confirmButton={{
                        text: "Elimina",
                        theme: "error",
                        onClick: deleteEventHandler,
                    }}
                >
                    <p className="body-medium">
                        Sei sicuro di voler eliminare la prenotazione?
                    </p>
                </ModalDialogComponent>
            </BookEventContainer>
        );
    }
);

export default EventShow;
